import { useMemo, useState } from 'react';
import {
  useAdminStatsQuery,
  useAnalyticsNetworkSummaryQuery,
  useFbcPerformanceQuery,
  useGbpNetworkSummaryQuery,
  useScorecardQuery,
  useTopBottomQuery,
} from '../api/queries';
import BackLink from '../components/BackLink';
import DateRangePicker from '../components/DateRangePicker';
import FbcCardGrid from '../components/FbcCardGrid';
import FbcPortfolioCharts from '../components/FbcPortfolioCharts';
import AdAllocationSection from '../components/AdAllocationSection';
import GbpReviewsSection from '../components/GbpReviewsSection';
import KpiTile from '../components/KpiTile';
import NetworkRankingsMap from '../components/NetworkRankingsMap';
import RankingsSection from '../components/RankingsSection';
import WebsiteTrafficSection from '../components/WebsiteTrafficSection';
import ScorecardTable from '../components/ScorecardTable';
import TopBottomTables from '../components/TopBottomTables';
import { useAuth } from '../hooks/useAuth';
import { formatDuration, formatNumber, formatPercent } from '../lib/format';
import UsersView from './UsersView';

type Props = {
  /** When set, scope all data to this FBC's franchisees (admin viewing-as-fbc, or FBC viewing self). */
  fbcName?: string;
  onDrillFranchisee: (afNumber: string) => void;
  onDrillFbc?: (fbcName: string) => void;
  title?: string;
  subtitle?: string;
  /** Optional in-app back link. Only passed when there's a parent context
   *  to return to (e.g. admin viewing-as-fbc → Network Overview). */
  onBackHome?: () => void;
  backHomeLabel?: string;
};

function todayIso() { return new Date().toISOString().slice(0, 10); }
function jan1Iso() { return `${new Date().getFullYear()}-01-01`; }

export default function NetworkView({
  fbcName,
  onDrillFranchisee,
  onDrillFbc,
  title = 'Network Dashboard',
  subtitle = 'Performance signals and at-risk franchisees across the Amada Senior Care network',
  onBackHome,
  backHomeLabel,
}: Props) {
  const { user } = useAuth();
  const [adminTab, setAdminTab] = useState<'dashboard' | 'users'>('dashboard');
  const [dateFrom, setDateFrom] = useState(jan1Iso());
  const [dateTo, setDateTo] = useState(todayIso());
  const showAdminSubtabs = user?.role === 'admin' && !fbcName;

  const statsQ = useAdminStatsQuery(dateFrom, dateTo, !fbcName);
  // Network-wide GBP roll-up — admin-only and unscoped (the endpoint window
  // is always "current 90d vs prior 90d", independent of the date picker).
  const gbpNetworkQ = useGbpNetworkSummaryQuery(user?.role === 'admin' && !fbcName);
  const analyticsNetworkQ = useAnalyticsNetworkSummaryQuery(user?.role === 'admin' && !fbcName);
  // Always fetch fbcPerf — needed both for the network-view FBC card grid and
  // for the per-FBC "Google Ads Call Metrics" KPI strip when scoped.
  const fbcPerfQ = useFbcPerformanceQuery(
    dateFrom,
    dateTo,
    user?.role === 'admin' || user?.role === 'fbc',
  );
  const scorecardQ = useScorecardQuery(dateFrom, dateTo, true);
  const topBottomQ = useTopBottomQuery(dateFrom, dateTo, !fbcName);

  const filteredScorecard = useMemo(() => {
    if (!scorecardQ.data) return [];
    if (!fbcName) return scorecardQ.data;
    return scorecardQ.data.filter((r) => r.fbcName === fbcName);
  }, [scorecardQ.data, fbcName]);

  const summary = useMemo(() => {
    const rows = filteredScorecard;
    const green = rows.filter((r) => r.status === 'green').length;
    const yellow = rows.filter((r) => r.status === 'yellow').length;
    const red = rows.filter((r) => r.status === 'red').length;
    const totalCalls = rows.reduce((s, r) => s + (Number(r.totalCalls) || 0), 0);
    const assessments = rows.reduce(
      (s, r) => s + (Number(r.assessmentsBooked) || 0),
      0,
    );
    const interviews = rows.reduce(
      (s, r) => s + (Number(r.interviewsScheduled) || 0),
      0,
    );
    return { green, yellow, red, totalCalls, assessments, interviews };
  }, [filteredScorecard]);

  // FBC-specific stats from fbcPerf when scoped
  const fbcStats = useMemo(() => {
    if (!fbcName || !fbcPerfQ.data) return null;
    return fbcPerfQ.data.find((f) => f.fbcName === fbcName) ?? null;
  }, [fbcPerfQ.data, fbcName]);

  if (scorecardQ.isLoading) {
    return (
      <div className="listings-loading">
        <div className="spinner" />
        Loading dashboard...
      </div>
    );
  }
  if (scorecardQ.error) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#c0392b' }}>
        Error loading dashboard: {(scorecardQ.error as Error).message}
      </div>
    );
  }

  // Network-wide stats use Pulse aggregate stats; FBC-scoped uses scorecard rollup
  const totalCalls = fbcName
    ? Number(fbcStats?.totalCalls) || summary.totalCalls
    : Number(statsQ.data?.totalCalls) || 0;
  const answeredCalls = fbcName
    ? Math.round((totalCalls * (Number(fbcStats?.answerRate) || 0)) / 100)
    : Number(statsQ.data?.answeredCalls) || 0;
  const missed = Number(statsQ.data?.missedCalls) || 0;
  const avgDur = Number(statsQ.data?.avgDuration) || 0;
  const networkAnswerRate =
    totalCalls > 0 ? Math.round((answeredCalls / totalCalls) * 100) : 0;

  if (showAdminSubtabs && adminTab === 'users') {
    return (
      <>
        <div className="ds-subtab-bar">
          <button
            type="button"
            className="ds-subtab"
            onClick={() => setAdminTab('dashboard')}
          >
            Dashboard
          </button>
          <button type="button" className="ds-subtab active">
            Users
          </button>
        </div>
        <UsersView />
      </>
    );
  }

  // ── FBC-scoped view (FBC viewing self OR admin drilled into FBC) ──────────
  if (fbcName) {
    const fbcAnswerRate = Number(fbcStats?.answerRate) || 0;
    const qualityRate = Number(fbcStats?.qualityRate) || 0;
    const bookingRate = Number(fbcStats?.bookingRate) || 0;
    const totalForBar = filteredScorecard.length;

    const isViewingSelf = user?.role === 'fbc';
    const tableHeading = isViewingSelf ? 'My Franchisees' : 'Assigned Franchisees';

    return (
      <>
        {onBackHome && backHomeLabel && (
          <BackLink label={backHomeLabel} onClick={onBackHome} />
        )}
        <div className="ds-view-header">
          <div className="ds-view-header-title">
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
          <div className="ds-view-header-actions">
            <DateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChange={({ dateFrom: f, dateTo: t }) => {
                setDateFrom(f);
                setDateTo(t);
              }}
            />
          </div>
        </div>

        <div className="ds-section-header" style={{ marginBottom: 12 }}>
          <h2>
            Google Ads Call Metrics{' '}
            <span style={{ fontSize: 13, fontWeight: 400, color: '#64748b' }}>
              (Last 90 Days)
            </span>
          </h2>
        </div>
        <div className="ds-kpi-grid ds-kpi-grid--7">
          <KpiTile
            label="Total Calls"
            value={formatNumber(totalCalls)}
            sub="Period total"
          />
          <KpiTile
            label="Answer Rate"
            value={`${fbcAnswerRate}%`}
            sub="Business hours (Mon-Fri, 8a-6p local)"
          />
          <KpiTile label="Quality Rate" value={`${qualityRate}%`} sub="Qualified leads" />
          <KpiTile
            label="Booking Rate"
            value={`${bookingRate}%`}
            sub="Assessments booked"
          />
          <KpiTile
            label="Assessments"
            value={formatNumber(summary.assessments)}
            sub="Booked"
          />
          <KpiTile
            label="Interviews"
            value={formatNumber(summary.interviews)}
            sub="Scheduled"
          />
          <KpiTile
            label="Health"
            value={`${summary.green}/${totalForBar}`}
            sub={
              <>
                <span className="delta-up">{summary.green} healthy</span> ·{' '}
                <span className="delta-down">{summary.red} risk</span>
              </>
            }
          />
        </div>

        {filteredScorecard.length > 0 && (
          <>
            <div className="ds-section-header">
              <h2>
                Answer Rates for Google Ads Participants{' '}
                <span style={{ fontSize: 13, fontWeight: 400, color: '#64748b' }}>
                  (Last 90 Days)
                </span>
              </h2>
              <span className="meta">Calls and answer rate per franchisee</span>
            </div>
            <FbcPortfolioCharts rows={filteredScorecard} />
          </>
        )}

        <NetworkRankingsMap
          onDrillIn={onDrillFranchisee}
          fbcFilter={fbcName}
          title={`Grid Search Coverage — ${isViewingSelf ? 'My Portfolio' : fbcName}`}
        />

        {filteredScorecard.length > 0 ? (
          <>
            <div className="ds-section-header">
              <h2>
                {tableHeading}{' '}
                <span style={{ fontSize: 13, fontWeight: 400, color: '#64748b' }}>
                  (Last 90 Days, 3+ Calls)
                </span>
              </h2>
              <span className="meta">
                {filteredScorecard.length} with 3+ calls · click to drill in
              </span>
            </div>
            <ScorecardTable
              rows={filteredScorecard}
              showFbcColumn={false}
              onDrillIn={onDrillFranchisee}
            />
          </>
        ) : (
          <div
            className="ds-panel"
            style={{ padding: '60px 24px', textAlign: 'center', color: '#8a95a5' }}
          >
            No franchisee data for this FBC in the selected period.
          </div>
        )}
      </>
    );
  }

  // ── Network-wide view (admin only) ────────────────────────────────────────
  return (
    <>
      {showAdminSubtabs && (
        <div className="ds-subtab-bar">
          <button
            type="button"
            className={`ds-subtab ${adminTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setAdminTab('dashboard')}
          >
            Dashboard
          </button>
          <button
            type="button"
            className={`ds-subtab ${adminTab === 'users' ? 'active' : ''}`}
            onClick={() => setAdminTab('users')}
          >
            Users
          </button>
        </div>
      )}

      <div className="ds-view-header">
        <div className="ds-view-header-title">
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        <div className="ds-view-header-actions">
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={({ dateFrom: f, dateTo: t }) => {
              setDateFrom(f);
              setDateTo(t);
            }}
          />
        </div>
      </div>

      {user?.role === 'admin' && gbpNetworkQ.data && (
        <>
          <div className="ds-section-header" style={{ marginBottom: 12 }}>
            <h2>
              Google Business Profile — Network Totals{' '}
              <span style={{ fontSize: 13, fontWeight: 400, color: '#64748b' }}>
                {gbpNetworkQ.data.dateRange
                  ? `(${gbpNetworkQ.data.dateRange})`
                  : '(Current 90 days vs prior 90 days)'}
              </span>
            </h2>
            <span className="meta">
              {gbpNetworkQ.data.locationsWithData} of{' '}
              {gbpNetworkQ.data.locationsTotal} locations reporting
            </span>
          </div>
          <div className="ds-kpi-grid">
            {(
              [
                { key: 'totalViews',        label: 'Views' },
                { key: 'viewsMaps',         label: 'Views: Maps' },
                { key: 'activity',          label: 'Activity' },
                { key: 'drivingDirections', label: 'Driving Directions' },
                { key: 'phoneCalls',        label: 'Phone Calls' },
              ] as const
            ).map(({ key, label }) => {
              const m = gbpNetworkQ.data!.metrics[key];
              const change = m.change;
              const deltaClass =
                change == null ? '' : change >= 0 ? 'delta-up' : 'delta-down';
              return (
                <KpiTile
                  key={key}
                  label={label}
                  value={formatNumber(m.current)}
                  sub={
                    change == null ? (
                      <span style={{ color: '#8a95a5' }}>vs prior period: —</span>
                    ) : (
                      <>
                        vs prior period:{' '}
                        <span className={deltaClass}>{formatPercent(change)}</span>
                      </>
                    )
                  }
                />
              );
            })}
          </div>
        </>
      )}

      {user?.role === 'admin' && analyticsNetworkQ.data && (
        <>
          <div className="ds-section-header" style={{ marginBottom: 12 }}>
            <h2>
              Organic Website Performance — Network Totals{' '}
              <span style={{ fontSize: 13, fontWeight: 400, color: '#64748b' }}>
                {analyticsNetworkQ.data.dateRange
                  ? `(${analyticsNetworkQ.data.dateRange})`
                  : '(Current 90 days vs prior 90 days)'}
              </span>
            </h2>
            <span className="meta">
              {analyticsNetworkQ.data.locationsWithData} of{' '}
              {analyticsNetworkQ.data.locationsTotal} locations reporting
            </span>
          </div>
          <div className="ds-kpi-grid">
            {(
              [
                { key: 'sessions',           label: 'Sessions' },
                { key: 'chats',              label: 'Chats' },
                { key: 'phoneClicks',        label: 'Phone Clicks' },
                { key: 'webformSubmissions', label: 'Webform Submissions' },
              ] as const
            ).map(({ key, label }) => {
              const m = analyticsNetworkQ.data!.metrics[key];
              const change = m.change;
              const deltaClass =
                change == null ? '' : change >= 0 ? 'delta-up' : 'delta-down';
              return (
                <KpiTile
                  key={key}
                  label={label}
                  value={formatNumber(m.current)}
                  sub={
                    change == null ? (
                      <span style={{ color: '#8a95a5' }}>vs prior period: —</span>
                    ) : (
                      <>
                        vs prior period:{' '}
                        <span className={deltaClass}>{formatPercent(change)}</span>
                      </>
                    )
                  }
                />
              );
            })}
          </div>
        </>
      )}

      {user?.role === 'admin' && <GbpReviewsSection onDrillIn={onDrillFranchisee} />}

      <div className="ds-section-header" style={{ marginBottom: 12 }}>
        <h2>Inbound Phone Calls — Paid Performance</h2>
        <span className="meta">Call volume and outcomes from Google Ads in the selected period</span>
      </div>
      <div className="ds-kpi-grid ds-kpi-grid--8">
        <KpiTile
          label="Total Calls"
          value={formatNumber(totalCalls)}
          sub={`${formatNumber(answeredCalls)} answered`}
        />
        <KpiTile
          label="Answer Rate"
          value={`${networkAnswerRate}%`}
          sub="Business hours (Mon-Fri, 8a-6p local)"
        />
        <KpiTile
          label="Missed Calls"
          value={formatNumber(missed)}
          sub="Opportunities lost"
        />
        <KpiTile
          label="Avg Duration"
          value={formatDuration(avgDur)}
          sub="Per connected call"
        />
        <KpiTile
          label="Assessments"
          value={formatNumber(summary.assessments)}
          sub="Booked"
        />
        <KpiTile
          label="Interviews"
          value={formatNumber(summary.interviews)}
          sub="Scheduled"
        />
        {fbcPerfQ.data && (
          <KpiTile
            label="Active FBCs"
            value={fbcPerfQ.data.length}
            sub="Coaching network"
          />
        )}
        <KpiTile
          label="Franchisees"
          value={filteredScorecard.length}
          sub={
            <>
              <span className="delta-up">{summary.green} healthy</span> ·{' '}
              <span className="delta-down">{summary.red} risk</span>
            </>
          }
        />
      </div>

      {user?.role === 'admin' && <NetworkRankingsMap onDrillIn={onDrillFranchisee} />}
      {user?.role === 'admin' && <RankingsSection onDrillIn={onDrillFranchisee} />}
      {user?.role === 'admin' && <WebsiteTrafficSection onDrillIn={onDrillFranchisee} />}
      {user?.role === 'admin' && <AdAllocationSection onDrillIn={onDrillFranchisee} />}

      {fbcPerfQ.data && fbcPerfQ.data.length > 0 && onDrillFbc && (
        <>
          <div className="ds-section-header">
            <h2>FBC Performance</h2>
            <span className="meta">
              {fbcPerfQ.data.length} coaches · click to view portfolio
            </span>
          </div>
          <FbcCardGrid fbcs={fbcPerfQ.data} onDrillIn={onDrillFbc} />
        </>
      )}

      {topBottomQ.data && (
        <TopBottomTables data={topBottomQ.data} onDrillIn={onDrillFranchisee} />
      )}

      {filteredScorecard.length > 0 && (
        <>
          <div className="ds-section-header">
            <h2>
              All Franchisees{' '}
              <span style={{ fontSize: 13, fontWeight: 400, color: '#64748b' }}>
                (Last 90 Days, 3+ Calls)
              </span>
            </h2>
            <span className="meta">
              {filteredScorecard.length} total · {summary.green} healthy ·{' '}
              {summary.yellow} warning · {summary.red} at risk
            </span>
          </div>
          <ScorecardTable
            rows={filteredScorecard}
            showFbcColumn={true}
            onDrillIn={onDrillFranchisee}
          />
        </>
      )}
    </>
  );
}
